import { Suspense, type ReactElement, type ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'

import { ErrorFallback } from '@/components/common/ErrorFallback'
import { PageLoader } from '@/components/common/PageLoader'
import { reportError } from '@/lib/logger'

interface ModuleBoundaryProps {
  /** Identifies the failing module in logs — use the route or feature name. */
  name: string
  children: ReactNode
  fallback?: ReactNode
}

/**
 * Wrap every route and every independently-failing feature in one of these.
 * A crash inside stays local: the rest of the app keeps working, and
 * "retry" also resets any failed TanStack Query in the subtree.
 */
export function ModuleBoundary({ name, children, fallback }: ModuleBoundaryProps): ReactElement {
  const { reset } = useQueryErrorResetBoundary()

  return (
    <ErrorBoundary
      onReset={reset}
      FallbackComponent={ErrorFallback}
      onError={(error, info) => {
        reportError(error, { module: name, componentStack: info.componentStack })
      }}
    >
      <Suspense fallback={fallback ?? <PageLoader />}>{children}</Suspense>
    </ErrorBoundary>
  )
}
